// src/pages/Auth.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// base API cohérente avec axiosInstance
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(
  /\/+$/,
  ''
);
const API_URL = `${API_BASE}/api`;

// ID de conversion Google Ads "Inscription FlowDevis"
const ADS_CONVERSION_ID = 'AW-17760901331/2q-XCL_SrMcbENOxh5VC';

/**
 * Helper : tracking inscription / connexion
 * - GA4 : event fd_signup (toujours, pour analytics)
 * - Google Ads :
 *    - event "conversion" UNIQUEMENT sur vraie inscription email/mot de passe
 *    - envoi de l'email pour les conversions améliorées (enhanced conversions)
 */
function trackSignup(method, emailForAds) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    // 1) Event GA4 générique
    window.gtag('event', 'fd_signup', {
      method,
    });

    // 2) Conversion Google Ads uniquement pour inscription email + mot de passe
    if (method === 'email_password' && emailForAds) {
      const normalizedEmail = String(emailForAds).trim().toLowerCase();

      // a) Conversions améliorées : on fournit l'email à Google
      window.gtag('set', 'user_data', {
        email: normalizedEmail,
      });

      // b) Évènement de conversion
      window.gtag('event', 'conversion', {
        send_to: ADS_CONVERSION_ID,
        // value: 0.0,
        // currency: 'EUR',
      });
    }
  } catch (e) {
    console.warn('Erreur tracking signup', e);
  }
}

// style bouton principal (identique au reste de l’app)
const primaryBtn =
  'w-full inline-flex items-center justify-center h-10 px-4 rounded-full ' +
  'bg-sky-700 text-white text-sm font-medium shadow-sm hover:bg-sky-600 ' +
  'disabled:opacity-60 disabled:cursor-default';

export default function Auth() {
  // login / register / forgot
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // afficher / masquer le mot de passe
  const [showPassword, setShowPassword] = useState(false);

  // mémoriser les identifiants
  const [rememberCreds, setRememberCreds] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ----- Pré-remplissage depuis localStorage -----
  useEffect(() => {
    try {
      const raw = localStorage.getItem('fd_auth_remember');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (parsed.email) setEmail(parsed.email);
          if (parsed.password) setPassword(parsed.password);
          setRememberCreds(true);
        }
      }
    } catch (e) {
      console.warn('Impossible de lire fd_auth_remember', e);
    }
  }, []);

  // ----- Callback Google : récupère le token dans ?token=... ou #token=... -----
  useEffect(() => {
    let token = null;

    // 1) Hash : /login#token=xxxx (ancien comportement)
    if (window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      token = hashParams.get('token');
    }

    // 2) Query string : /login?token=xxxx (nouveau comportement)
    if (!token) {
      const searchParams = new URLSearchParams(location.search);
      token = searchParams.get('token') || searchParams.get('googleToken');
    }

    if (token) {
      console.log('Auth.jsx – token Google trouvé :', token.slice(0, 20) + '...');
      localStorage.setItem('token', token);
      toast.success('Connexion Google réussie !');

      // Tracking : connexion / inscription via Google
      // → GA4 uniquement (pas de conversion Ads tant qu’on ne distingue pas login vs signup côté backend)
      trackSignup('google_oauth', null);

      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  // ----- Gestion des erreurs Google (?err=google|server) -----
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('err');
    if (err === 'google') {
      toast.error('Connexion Google annulée ou refusée.');
    } else if (err === 'server') {
      toast.error('Erreur serveur pendant la connexion Google.');
    }
  }, [location]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
  };

  // ----- Connexion classique -----
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);

        // gestion du "Enregistrer mon mot de passe"
        try {
          if (rememberCreds) {
            localStorage.setItem(
              'fd_auth_remember',
              JSON.stringify({ email, password })
            );
          } else {
            localStorage.removeItem('fd_auth_remember');
          }
        } catch (e2) {
          console.warn('Impossible de stocker fd_auth_remember', e2);
        }

        toast.success('Connexion réussie !');
        navigate('/');
      } else {
        toast.error(data.message || 'Erreur lors de la connexion');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur serveur');
    }
  };

  // ----- Inscription -----
  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Tracking GA4 + Google Ads : inscription réussie (email + mot de passe)
        trackSignup('email_password', email);

        if (data.token) {
          localStorage.setItem('token', data.token);
          toast.success('Compte créé avec succès, vous êtes connecté !');
          resetForm();
          navigate('/');
        } else {
          toast.success('Compte créé avec succès, veuillez vous connecter.');
          resetForm();
          setMode('login');
        }
      } else {
        toast.error(data.message || "Erreur lors de l'inscription");
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur serveur');
    }
  };

  // ----- Mot de passe oublié -----
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.warning('Merci de renseigner votre email.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          data.message ||
            "Si un compte existe avec cet email, un lien de réinitialisation a été envoyé."
        );
        setMode('login');
      } else {
        toast.error(
          data.message || 'Erreur lors de la demande de réinitialisation'
        );
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur serveur');
    }
  };

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot';

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          'radial-gradient(circle at top left, #e0edff 0, #ffffff 55%, #f5f7fb 100%)',
      }}
    >
      {/* Bandeau haut avec logo cliquable vers le site */}
      <header className="w-full px-4 py-4 flex items-center justify-between max-w-5xl mx-auto">
        <a
          href="https://flowdevis.fr"
          className="inline-flex items-center gap-2"
        >
          <span className="text-2xl font-extrabold text-gray-900">
            FlowDevis
          </span>
          <img
            src="/icon-512.png"
            alt="FlowDevis"
            className="h-7 w-7 rounded-lg"
          />
        </a>

        <a
          href="https://flowdevis.fr"
          className="text-xs text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline"
        >
          Retour au site
        </a>
      </header>

      {/* Contenu centré */}
      <main className="flex-1 flex items-center justify-center px-4 pb-10">
        {/* Grid 2 colonnes sur desktop, 1 colonne sur mobile */}
        <div className="w-full max-w-5xl grid gap-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-stretch">
          {/* Colonne gauche : bloc marketing avec dégradé inline */}
          <aside
            className="hidden md:flex flex-col justify-center rounded-3xl shadow-lg border"
            style={{
              background:
                'radial-gradient(circle at top left, #e0edff 0, #ffffff 55%, #f5f7fb 100%)',
              borderColor: 'rgba(148, 163, 184, 0.4)',
            }}
          >
            <div className="px-8 py-10">
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Votre facturation, rapide, pro… et assistée par l’IA.
              </h1>

              <p className="text-sm text-gray-700 mb-4">
                FlowDevis centralise vos devis et factures et intègre un assistant IA
                qui vous aide à décrire vos travaux, rédiger vos e-mails clients
                et gagner du temps sur l’administratif.
              </p>

              <ul className="space-y-2 text-sm text-gray-800">
                <li>• Devis &amp; factures en quelques clics</li>
                <li>• Textes de devis et e-mails générés par IA en 1 clic</li>
                <li>• PDF A4 propres avec mentions légales automatiques</li>
                <li>• Relances et suivi des paiements simplifiés</li>
                <li>• 10 jours d’essai offerts, sans CB</li>
              </ul>

              <p className="mt-6 text-xs text-gray-500">
                En créant un compte, vous acceptez nos{' '}
                <a
                  href="https://flowdevis.fr/cgv/"
                  className="underline hover:text-gray-800"
                  target="_blank"
                  rel="noreferrer"
                >
                  CGV
                </a>{' '}
                et notre{' '}
                <a
                  href="https://flowdevis.fr/confidentialite/"
                  className="underline hover:text-gray-800"
                  target="_blank"
                  rel="noreferrer"
                >
                  politique de confidentialité
                </a>
                .
              </p>
            </div>
          </aside>

          {/* Colonne droite : carte Auth */}
          <section className="bg-white rounded-3xl shadow-lg border border-gray-200 px-6 py-8 md:px-8 md:py-10">
            {/* Onglets (login / register) */}
            <div className="flex justify-center mb-6">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`px-6 py-2 text-sm font-semibold rounded-full transition ${
                  isLogin
                    ? 'bg-sky-700 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                className={`ml-2 px-6 py-2 text-sm font-semibold rounded-full transition ${
                  isRegister
                    ? 'bg-sky-700 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Inscription
              </button>
            </div>

            {/* Bouton Google */}
            <a
              href={`${API_BASE}/api/auth/google`}
              className="mb-6 w-full inline-flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-full hover:bg-gray-50 transition"
            >
              <img
                alt=""
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                width="18"
                height="18"
              />
              <span className="text-sm font-medium text-gray-700">
                Continuer avec Google
              </span>
            </a>

            {/* ---------- Formulaire Connexion ---------- */}
            {isLogin && (
              <form onSubmit={handleLogin}>
                <h2 className="text-xl font-bold text-center text-gray-900 mb-5">
                  Connexion à votre espace
                </h2>

                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 mb-4 border rounded-lg text-sm"
                  required
                />

                {/* Champ mot de passe + icône œil */}
                <div className="mb-2 relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 pr-10 border rounded-lg text-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 text-xs"
                    aria-label={
                      showPassword
                        ? 'Masquer le mot de passe'
                        : 'Afficher le mot de passe'
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-8 0-1.27.3-2.47.84-3.53" />
                          <path d="M1 1l22 22" />
                          <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12C2.73 7.11 7 4 12 4s9.27 3.11 11 8c-1.73 4.89-6 8-11 8S2.73 16.89 1 12z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>

                {/* Case "Enregistrer mon mot de passe" + lien Mot de passe oublié */}
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={rememberCreds}
                      onChange={(e) => setRememberCreds(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Enregistrer mon mot de passe
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setPassword('');
                    }}
                    className="text-xs text-sky-700 hover:underline"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>

                <button type="submit" className={primaryBtn}>
                  Se connecter
                </button>
              </form>
            )}

            {/* ---------- Formulaire Inscription ---------- */}
            {isRegister && (
              <form onSubmit={handleRegister}>
                <h2 className="text-xl font-bold text-center text-gray-900 mb-5">
                  Créer votre compte FlowDevis
                </h2>

                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 mb-4 border rounded-lg text-sm"
                  required
                />
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 mb-6 border rounded-lg text-sm"
                  required
                />
                <button type="submit" className={primaryBtn}>
                  Créer le compte
                </button>
              </form>
            )}

            {/* ---------- Formulaire Mot de passe oublié ---------- */}
            {isForgot && (
              <form onSubmit={handleForgot}>
                <h2 className="text-xl font-bold text-center text-gray-900 mb-5">
                  Mot de passe oublié
                </h2>
                <p className="text-xs text-gray-600 mb-4">
                  Entrez votre adresse e-mail. Si un compte existe, vous recevrez
                  un lien sécurisé pour choisir un nouveau mot de passe.
                </p>
                <input
                  type="email"
                  placeholder="Votre email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 mb-4 border rounded-lg text-sm"
                  required
                />

                <div className="flex justify-between items-center mb-4">
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-xs text-gray-600 hover:underline"
                  >
                    Retour à la connexion
                  </button>
                </div>

                <button type="submit" className={primaryBtn}>
                  Envoyer le lien de réinitialisation
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
